import React, {
  forwardRef,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useImperativeHandle,
  useState,
} from 'react';
import merge from 'deepmerge';
import { Template } from 'devextreme-react';
import { NuvoForm } from '@nuvolo/nuux-core/components/NuvoForm';
import mapFieldItems, { flattenMappedFieldItems } from './mapFieldItems';
import type { FormProps, FormRefType } from './FormProps';
import renderItems from './renderItems';
import Styles from './Styles';
import dot from './templates/dot';
import dxFormComponent from 'devextreme-react/form';
import TenantSelector from '../TenantSelector';
import { Tenant } from '#/graphql/types';
import _, { get } from 'lodash';
import useSearchQuery from '#/common/hooks/useSearchQuery';
import { clearFilters } from '#/common/utils/clearFilters';
import { uuidPattern } from '#/common/utils/patterns/uuid';
import WorkingOnItThrobber from '#/app/components/Throbbers/WorkingOnItThrobber';
import deepMapChildren from '#/common/utils/deepMapChildren';
import { PageActions } from '../SubPage';
import mapPageActions from './mapPageActions';
import { useSearchParams } from 'react-router-dom';

const NuvoFormWrapper = forwardRef<dxFormComponent, React.ComponentProps<typeof NuvoForm>>(
  (props, ref) => {
    return <NuvoForm {...(props as any)} ref={ref} />;
  },
);

export const Throbber = () => (
  <>
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        background: 'grey',
        width: '100%',
        height: '100%',
        opacity: 0.5,
        zIndex: 1000,
        overflow: 'hidden',
        touchAction: 'none',
        overscrollBehavior: 'contain',
      }}></div>
    <div
      style={{
        position: 'absolute',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        top: '50vh',
        zIndex: '9999',
      }}>
      <WorkingOnItThrobber />
    </div>
  </>
);

const Form = <T,>(
  {
    record,
    readonly,
    fields = [],
    actions,
    supportTenant,
    onInitialized,
    onContentReady,
    onFieldDataChanged,
    children,
  }: FormProps<T>,
  ref: React.ForwardedRef<FormRefType>,
) => {
  const [searchParams] = useSearchParams();
  const formRef = useRef<dxFormComponent>(null);
  const isDirtyRef = useRef(false);

  useImperativeHandle(ref, () => {
    const instance = formRef?.current?.instance as FormRefType;
    instance.isDirty = isDirtyRef.current;
    return instance;
  }, []);

  const id = get(record, 'id');
  const isNavBackRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const setLoading = useCallback((isLoading: boolean) => {
    setIsLoading(isLoading);
    formRef.current?.instance.option('disabled', isLoading);
    formRef.current?.instance.option('readOnly', isLoading);
    formRef.current?.instance.repaint();
  }, []);

  const query = useSearchQuery();

  const fieldItems = useMemo(() => {
    const items = mapFieldItems(fields);
    const newItems = deepMapChildren(items, (item) => {
      const val = get(record, item.dataField);
      if (item.dataField && val === null) {
        item.editorOptions = item.editorOptions ?? {};
        item.editorOptions.value = undefined;
      }
      return item;
    });
    return newItems;
  }, [fields, record]);

  // Use local state for formData to handle field value changes
  const [formData, setFormData] = useState(() => {
    const fieldItemsUngrouped = flattenMappedFieldItems(fieldItems);
    const recordProperties = fieldItemsUngrouped.map((fieldItem) => ({
      dataField: fieldItem.dataField,
      dataType: fieldItem.editorOptions?.type || 'string',
    }));

    const { filtersClean } = clearFilters(query.filters, recordProperties);
    // Filters are arrays. If there are no filter elements, then there must only be one filter
    // and it is the whole filtersClean object
    // (we get ['tenantId', '=', ${id}], not [['tenantId', '=', ${id}]] unlike when there are multiple filters)
    const filtersFormatted = filtersClean.some((el) => Array.isArray(el))
      ? filtersClean
      : [filtersClean];
    // AND string or any other non-array doesn't interest us. Same goes for filters that
    // say anything other than [${varName}, '=', ${something}]
    const filtersToUse = filtersFormatted.filter(
      (filter) => Array.isArray(filter) && filter[1] === '=',
    );

    const keyValuePairsFromQuery = filtersToUse.map(([propertyName, _, propertyValue]) => [
      propertyName,
      propertyValue,
    ]);

    const queryValuesAsObj = keyValuePairsFromQuery.reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {} as Partial<typeof record>,
    );

    // URL overrides record
    return merge<T>({ ...record }, queryValuesAsObj);
  });

  // Prioritize tenantId from URL over formData
  const tenantIdFromUrl = searchParams.get('tenantId') ?? undefined;
  const tenantIdFromForm = get(formData, 'tenantId') as string | undefined;
  const tenantId = tenantIdFromUrl || tenantIdFromForm;

  // ESSENTIAL: hydrate tenantId from URL into actual formData + dxForm so submit won't inject another tenant
  useEffect(() => {
    if (!id && supportTenant && tenantIdFromUrl && !tenantIdFromForm) {
      setFormData((prev) => ({ ...prev, tenantId: tenantIdFromUrl }));
      formRef.current?.instance.updateData('tenantId', tenantIdFromUrl);
      formRef.current?.instance.repaint();
    }
  }, [id, supportTenant, tenantIdFromUrl, tenantIdFromForm]);

  // Reset dirty state when record changes (new form loaded)
  useEffect(() => {
    isDirtyRef.current = false;
    isNavBackRef.current = false;
  }, [record]);

  // Helper function to check if navigation is from "new" form to edit form (successful creation)
  const isSuccessfulCreation = useCallback((currentPath: string, nextPath: string) => {
    // Handle different "new" form patterns:
    // 1. /module/new -> /module/uuid/details
    // 2. /module/new/details -> /module/uuid/details

    let basePath = '';

    if (currentPath.endsWith('/new/details')) {
      // Pattern: /module/new/details -> /module/uuid/details
      basePath = currentPath.replace('/new/details', '');
    } else if (currentPath.endsWith('/new')) {
      // Pattern: /module/new -> /module/uuid/details
      basePath = currentPath.replace('/new', '');
    } else {
      // Not a "new" form
      return false;
    }

    // Escape special regex characters in the base path
    const escapedBasePath = basePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create pattern to match: /basePath/uuid or /basePath/uuid/details

    const successPattern = new RegExp(`^${escapedBasePath}/${uuidPattern}(?:/details)?$`, 'i');

    return successPattern.test(nextPath);
  }, []);

  // Block React Router navigation when form is dirty
  // const shouldBlock = useCallback<BlockerFunction>(
  //   ({ currentLocation, nextLocation, historyAction }) => {
  //     if (historyAction === 'POP' && !isNavBackRef.current) {
  //       isNavBackRef.current = true;
  //     }

  //     // Don't block if form is not dirty, readonly or if we're staying on the same path
  //     if (!isDirtyRef.current || readonly || currentLocation.pathname === nextLocation.pathname) {
  //       return false;
  //     }

  //     // Don't block if this looks like a successful creation flow
  //     if (isSuccessfulCreation(currentLocation.pathname, nextLocation.pathname)) {
  //       // Reset dirty state since this is a successful creation
  //       isDirtyRef.current = false;
  //       return false;
  //     }

  //     // Block all other navigation when form is dirty
  //     return true;
  //   },
  //   [readonly, isSuccessfulCreation],
  // );

  // const blocker = useBlocker(shouldBlock);

  // Handle blocked navigation with toast confirmation
  // useEffect(() => {
  //   if (blocker.state === 'blocked') {
  //     // Handle React DOM navigation with confirmation dialog
  //     const handleConfirmation = async () => {
  //       try {
  //         const shouldLeave = await confirm(
  //           'warning',
  //           'Leave page? Changes you made may not be saved.',
  //           ['Cancel', 'Leave'],
  //         );

  //         if (shouldLeave) {
  //           blocker.proceed();
  //         } else {
  //           blocker.reset?.();
  //         }
  //       } catch (error) {
  //         // If there's an error with the confirmation, default to not leaving
  //         blocker.reset?.();
  //       }
  //     };

  //     handleConfirmation();

  //     if (isNavBackRef.current) {
  //       isNavBackRef.current = false;
  //     }
  //   }
  // }, [blocker.state, confirm]);

  // Set up beforeunload event listener for external navigation (browser close, refresh, etc.)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Handle back navigation in React DOM instead of the browser
      if (isNavBackRef.current) {
        e.preventDefault();
        return undefined;
      }

      if (isDirtyRef.current && !readonly) {
        // Set the message for older browsers that still show custom messages
        e.returnValue = 'Leave site? Changes you made may not be saved.';
        // Return the message for compatibility
        return 'Leave site? Changes you made may not be saved.';
      }

      // If form is not dirty, allow navigation without warning
      return undefined;
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [readonly]);

  // Enhanced field data changed handler
  const handleFieldDataChanged = useCallback(
    (e: any) => {
      // Mark form as dirty when any field changes
      if (!isDirtyRef.current && !readonly) {
        isDirtyRef.current = true;
      }

      // Update local formData state to keep it in sync with form changes
      if (e.dataField && e.value !== undefined) {
        setFormData((prevData) => ({
          ...prevData,
          [e.dataField]: e.value,
        }));
      }

      // Call the original onFieldDataChanged if provided
      if (onFieldDataChanged) {
        onFieldDataChanged(e);
      }
    },
    [readonly, onFieldDataChanged],
  );

  const selectTenant = useCallback((tenant: Tenant) => {
    formRef.current?.instance.updateData('tenantId', tenant.id);
    formRef.current?.instance.repaint();
  }, []);

  console.log(actions);
  const pageActions = useMemo(
    () => mapPageActions(actions, setLoading, formRef),
    [actions, setLoading],
  );

  return (
    <Styles>
      {isLoading && <Throbber />}
      {!id && supportTenant && !tenantId && <TenantSelector onTenantSelect={selectTenant} />}
      {pageActions && <PageActions actions={pageActions} />}
      <NuvoFormWrapper
        validationGroup="FormValidationGroup"
        ref={formRef}
        formData={formData}
        readOnly={readonly}
        onInitialized={onInitialized}
        onFieldDataChanged={handleFieldDataChanged}
        onContentReady={onContentReady}>
        {renderItems(fieldItems)}
        <Template name="dot" render={dot} />

        {children}
      </NuvoFormWrapper>
    </Styles>
  );
};

export default React.memo(forwardRef(Form)) as typeof Form;
